import React, { useEffect, useRef, useState } from "react";
import { useJsApiLoader, StandaloneSearchBox } from "@react-google-maps/api";
import { TextField, InputAdornment, IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { FcGoogle } from "react-icons/fc";
import { addressFormatter } from "utils/commonFunctions";
import SHOW_TOAST from "utils/showToast";

const LIBRARIES: ("places")[] = ["places"];

type Props = {
  addressHandler: (address: ReturnType<typeof addressFormatter>) => void;
  label?: string;
};

const GoogleAutComplete = ({
  addressHandler,
  label = "Find address",
}: Props) => {
  const inputRef = useRef<any>(null);
  const [manualInput, setManualInput] = useState("");

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY || "",
    libraries: LIBRARIES,
  });

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `.pac-container { z-index: 20000 !important; }`;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handleOnPlaceChanged = () => {
    const places = inputRef.current?.getPlaces?.();
    if (places?.[0]) {
      const formatted = addressFormatter(places[0]);
      setManualInput(formatted.formattedAddress || places[0].formatted_address || "");
      addressHandler(formatted);
    }
  };

  const parseLatLng = (input: string) => {
    const regex =
      /^\s*(-?\d+(\.\d+)?)[°\s]*([NnSs])?,?\s*(-?\d+(\.\d+)?)[°\s]*([EeWw])?\s*$/;
    const match = input.match(regex);
    if (!match) return null;

    let lat = parseFloat(match[1]);
    const latDir = match[3]?.toUpperCase();
    let lng = parseFloat(match[4]);
    const lngDir = match[6]?.toUpperCase();

    if (latDir === "S") lat = -Math.abs(lat);
    if (latDir === "N") lat = Math.abs(lat);
    if (lngDir === "W") lng = -Math.abs(lng);
    if (lngDir === "E") lng = Math.abs(lng);

    return { lat, lng };
  };

  const handleManualSearch = () => {
    const coords = parseLatLng(manualInput);
    if (!coords || !isLoaded || !(window as any).google?.maps?.Geocoder) {
      return;
    }

    const geocoder = new (window as any).google.maps.Geocoder();
    geocoder.geocode({ location: coords }, (results: any[], status: string) => {
      if (status === "OK" && results?.[0]) {
        const formatted = addressFormatter(results[0]);
        setManualInput(formatted.formattedAddress || results[0].formatted_address || "");
        addressHandler(formatted);
      } else {
        SHOW_TOAST("No results found for this latitude/longitude.", "error");
      }
    });
  };

  if (!isLoaded) {
    return (
      <TextField
        fullWidth
        disabled
        label={label}
        placeholder="Loading Google Maps..."
      />
    );
  }

  return (
    <StandaloneSearchBox
      onLoad={(ref) => {
        inputRef.current = ref;
      }}
      onPlacesChanged={handleOnPlaceChanged}
    >
      <TextField
        fullWidth
        label={label}
        placeholder="Search address or enter lat,lng"
        value={manualInput}
        onChange={(e) => setManualInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleManualSearch();
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <FcGoogle size={20} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleManualSearch} edge="end" size="small">
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </StandaloneSearchBox>
  );
};

export default GoogleAutComplete;
